import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Package, Truck, MapPin, Clock, ShoppingCart } from "lucide-react";

const STATUS = {
  pending: { label: "En attente", color: "bg-slate-100 text-slate-700" },
  preparing: { label: "Préparation", color: "bg-amber-50 text-amber-800" },
  out_for_delivery: { label: "En livraison", color: "bg-sky-50 text-sky-800" },
  delivered: { label: "Livrée", color: "bg-emerald-50 text-emerald-800" },
  cancelled: { label: "Annulée", color: "bg-slate-100 text-slate-500" },
};

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [rxs, setRxs] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [form, setForm] = useState({ prescription_id: "", pharmacy_id: "", delivery_address: "", delivery: true });

  const load = async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = async () => {
    try {
      const [{ data: rxData }, { data: phData }] = await Promise.all([
        api.get("/prescriptions"),
        api.get("/pharmacies", { params: user.lat && user.lng ? { lat: user.lat, lng: user.lng } : {} }),
      ]);
      setRxs(rxData);
      setPharmacies(phData);
      setForm((f) => ({ ...f, delivery_address: user.address || "" }));
      setOpen(true);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/orders", form);
      toast.success("Commande envoyée à la pharmacie");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const advance = async (o, nextStatus) => {
    try {
      await api.patch(`/orders/${o.order_id}`, { status: nextStatus });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const isPharm = user?.role === "pharmacy";

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">{isPharm ? "Commandes reçues" : "Mes commandes"}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">{isPharm ? "Pharmacie" : "Médicaments"}</h1>
        </div>
        {!isPharm && (
          <Button onClick={openCreate} className="btn-primary" data-testid="new-order-btn"><ShoppingCart className="h-4 w-4 mr-2" />Commander avec une ordonnance</Button>
        )}
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500">
          <Package className="mx-auto h-10 w-10 text-slate-300 mb-3" />
          Aucune commande.
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const st = STATUS[o.status];
            return (
              <div key={o.order_id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`order-${o.order_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                      {o.delivery && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1"><Truck className="h-3 w-3" />Livraison</span>}
                    </div>
                    <div className="mt-2 font-display font-semibold">
                      {isPharm ? `Patient: ${o.patient?.name}` : o.pharmacy?.pharmacy_name || o.pharmacy?.name}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-1">
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{o.delivery_address}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(o.created_at).toLocaleString("fr-FR")}</span>
                    </div>
                    {o.driver_name && (
                      <div className="mt-2 text-xs text-emerald-700">🚴 Livreur : {o.driver_name} · ETA {o.eta_minutes} min</div>
                    )}
                    {o.prescription?.medications && (
                      <ul className="mt-3 text-sm text-slate-700 space-y-0.5">
                        {o.prescription.medications.map((m, i) => (
                          <li key={i}>• {m.name} <span className="text-slate-500">({m.quantity})</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {isPharm && (
                    <div className="flex flex-col gap-2">
                      {o.status === "pending" && <Button size="sm" onClick={() => advance(o, "preparing")} data-testid={`prepare-${o.order_id}`}>Préparer</Button>}
                      {o.status === "preparing" && o.delivery && <Button size="sm" onClick={() => advance(o, "out_for_delivery")} data-testid={`dispatch-${o.order_id}`}>Envoyer en livraison</Button>}
                      {o.status === "preparing" && !o.delivery && <Button size="sm" onClick={() => advance(o, "delivered")} data-testid={`pickup-${o.order_id}`}>Prête à retirer</Button>}
                      {o.status === "out_for_delivery" && <Button size="sm" onClick={() => advance(o, "delivered")} data-testid={`deliver-${o.order_id}`}>Livrée</Button>}
                      {!["delivered", "cancelled"].includes(o.status) && <Button size="sm" variant="ghost" onClick={() => advance(o, "cancelled")}>Annuler</Button>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Nouvelle commande</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label>Ordonnance</Label>
              <Select value={form.prescription_id} onValueChange={(v) => setForm({ ...form, prescription_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="order-rx-select"><SelectValue placeholder="Choisir une ordonnance" /></SelectTrigger>
                <SelectContent>
                  {rxs.map((r) => <SelectItem key={r.prescription_id} value={r.prescription_id}>{r.doctor?.name} — {new Date(r.created_at).toLocaleDateString("fr-FR")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pharmacie</Label>
              <Select value={form.pharmacy_id} onValueChange={(v) => setForm({ ...form, pharmacy_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="order-pharmacy-select"><SelectValue placeholder="Choisir une pharmacie" /></SelectTrigger>
                <SelectContent>
                  {pharmacies.map((p) => (
                    <SelectItem key={p.user_id} value={p.user_id}>
                      {p.pharmacy_name || p.name} {p.delivery_enabled && "· 🚴 Livraison"} {p.distance_km != null && `· ${p.distance_km}km`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Adresse de livraison</Label>
              <Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="mt-1.5" required data-testid="order-address-input" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="delivery" checked={form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.checked })} data-testid="order-delivery-check" />
              <Label htmlFor="delivery">Livraison à domicile (Premium)</Label>
            </div>
            <Button type="submit" className="w-full btn-primary" data-testid="order-submit-btn">Valider la commande</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
